// ============================================================
// Zero-Training AI deterministic Rubik's Cube solver
//
// No model, no training data and no probabilistic decisions.
// The solver validates the cubie state and uses a deterministic
// two-phase coordinate search with exact move and pruning tables.
// ============================================================

const FACE_NAMES = ['U', 'D', 'F', 'B', 'R', 'L'];
const MOVE_SUFFIXES = ['', '2', "'"];
const PHASE_TWO_GLOBAL_MOVES = [
    0, 1, 2,       // U, U2, U'
    3, 4, 5,       // D, D2, D'
    7,              // F2
    10,             // B2
    13,             // R2
    16              // L2
];

const SLICE_EDGE_FIRST = 8;
const SLICE_COMBINATION_COUNT = 495;
const TWIST_COUNT = 2187;
const FLIP_COUNT = 2048;
const CORNER_PERMUTATION_COUNT = 40320;
const EDGE_PERMUTATION_COUNT = 40320;
const SLICE_PERMUTATION_COUNT = 24;
const UNVISITED = 255;

const FACTORIAL = [1, 1, 2, 6, 24, 120, 720, 5040, 40320, 362880, 3628800, 39916800, 479001600];

// Each move cube says which old slot supplies a piece to each new slot,
// plus the orientation delta introduced by one clockwise quarter turn.
// These tables were derived from this application's body axes and the
// exact corner/edge ordering in cubeState.js.
const QUARTER_TURN_MOVE_CUBES = [
    // U
    {
        cp: [3, 0, 1, 2, 4, 5, 6, 7],
        co: [0, 0, 0, 0, 0, 0, 0, 0],
        ep: [1, 2, 3, 0, 4, 5, 6, 7, 8, 9, 10, 11],
        eo: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    },
    // D
    {
        cp: [0, 1, 2, 3, 5, 6, 7, 4],
        co: [0, 0, 0, 0, 0, 0, 0, 0],
        ep: [0, 1, 2, 3, 7, 4, 5, 6, 8, 9, 10, 11],
        eo: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    },
    // F
    {
        cp: [4, 1, 2, 0, 7, 5, 6, 3],
        co: [2, 0, 0, 1, 1, 0, 0, 2],
        ep: [9, 1, 2, 3, 8, 5, 6, 7, 0, 4, 10, 11],
        eo: [1, 0, 0, 0, 1, 0, 0, 0, 1, 1, 0, 0]
    },
    // B
    {
        cp: [0, 2, 6, 3, 4, 1, 5, 7],
        co: [0, 1, 2, 0, 0, 2, 1, 0],
        ep: [0, 1, 10, 3, 4, 5, 11, 7, 8, 9, 6, 2],
        eo: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 1]
    },
    // R
    {
        cp: [0, 1, 3, 7, 4, 5, 2, 6],
        co: [0, 0, 1, 2, 0, 0, 2, 1],
        ep: [0, 8, 2, 3, 4, 10, 6, 7, 5, 9, 1, 11],
        eo: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    },
    // L
    {
        cp: [1, 5, 2, 3, 0, 4, 6, 7],
        co: [1, 2, 0, 0, 2, 1, 0, 0],
        ep: [0, 1, 2, 11, 4, 5, 6, 9, 8, 3, 10, 7],
        eo: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    }
];

let cachedSolverTables = null;
let cachedInitializationMetrics = null;

export const notationForMoveIndex = moveIndex => (
    FACE_NAMES[Math.floor(moveIndex / 3)] + MOVE_SUFFIXES[moveIndex % 3]
);

export const moveIndexForNotation = notation => {
    if (typeof notation !== 'string') {
        return null;
    }

    const normalized = notation.trim().toUpperCase();
    if (!normalized) {
        return null;
    }

    const faceIndex = FACE_NAMES.indexOf(normalized.charAt(0));
    if (faceIndex < 0) {
        return null;
    }

    const suffix = normalized.substring(1);
    let powerIndex = 0;
    if (suffix === '2') {
        powerIndex = 1;
    } else if (suffix === "'") {
        powerIndex = 2;
    } else if (suffix !== '') {
        return null;
    }

    return faceIndex * 3 + powerIndex;
};

export const parseMoveSequence = sequence => {
    if (Array.isArray(sequence)) {
        return sequence.map(move => {
            if (typeof move === 'number') {
                return move >= 0 && move < 18 ? move : null;
            }
            return moveIndexForNotation(move);
        });
    }

    if (typeof sequence !== 'string') {
        return [];
    }

    const trimmed = sequence.trim();
    return trimmed ? trimmed.split(/\s+/).map(moveIndexForNotation) : [];
};

export const invertMoveIndex = moveIndex => {
    const faceBase = Math.floor(moveIndex / 3) * 3;
    const powerIndex = moveIndex % 3;
    return faceBase + (powerIndex === 0 ? 2 : (powerIndex === 2 ? 0 : 1));
};

export const invertMoveSequence = moves => moves.slice().reverse().map(invertMoveIndex);

export const createSolvedDiscreteState = () => ({
    corners: Array(8).fill(0).map((unused, piece) => ({ piece, ori: 0 })),
    edges: Array(12).fill(0).map((unused, piece) => ({ piece, ori: 0 })),
    isSolved: true
});

export const validateDiscreteCubeState = discreteState => {
    const errors = [];

    if (!discreteState || !Array.isArray(discreteState.corners) ||
        !Array.isArray(discreteState.edges)) {
        return {
            isValid: false,
            errors: ['Cube state is unavailable or incomplete.']
        };
    }

    if (discreteState.corners.length !== 8) {
        errors.push('Exactly eight corners are required.');
    }
    if (discreteState.edges.length !== 12) {
        errors.push('Exactly twelve edges are required.');
    }

    if (errors.length) {
        return { isValid: false, errors };
    }

    const cornerPieces = [];
    const edgePieces = [];
    let cornerOrientationSum = 0;
    let edgeOrientationSum = 0;

    discreteState.corners.forEach((corner, slotIndex) => {
        if (!corner || !isIntegerInRange(corner.piece, 0, 7)) {
            errors.push(`Corner slot ${slotIndex} has an invalid piece identity.`);
        } else {
            cornerPieces.push(corner.piece);
        }
        if (!corner || !isIntegerInRange(corner.ori, 0, 2)) {
            errors.push(`Corner slot ${slotIndex} has an invalid orientation.`);
        } else {
            cornerOrientationSum += corner.ori;
        }
    });

    discreteState.edges.forEach((edge, slotIndex) => {
        if (!edge || !isIntegerInRange(edge.piece, 0, 11)) {
            errors.push(`Edge slot ${slotIndex} has an invalid piece identity.`);
        } else {
            edgePieces.push(edge.piece);
        }
        if (!edge || !isIntegerInRange(edge.ori, 0, 1)) {
            errors.push(`Edge slot ${slotIndex} has an invalid orientation.`);
        } else {
            edgeOrientationSum += edge.ori;
        }
    });

    if (cornerPieces.length === 8 && !containsEveryIdentityExactlyOnce(cornerPieces, 8)) {
        errors.push('Corner pieces are duplicated or missing.');
    }
    if (edgePieces.length === 12 && !containsEveryIdentityExactlyOnce(edgePieces, 12)) {
        errors.push('Edge pieces are duplicated or missing.');
    }
    if (cornerOrientationSum % 3 !== 0) {
        errors.push('Corner-twist invariant failed: total twist must be divisible by three.');
    }
    if (edgeOrientationSum % 2 !== 0) {
        errors.push('Edge-flip invariant failed: total flip must be even.');
    }

    let cornerParity = null;
    let edgeParity = null;
    if (containsEveryIdentityExactlyOnce(cornerPieces, 8) &&
        containsEveryIdentityExactlyOnce(edgePieces, 12)) {
        cornerParity = permutationParity(cornerPieces);
        edgeParity = permutationParity(edgePieces);
        if (cornerParity !== edgeParity) {
            errors.push('Permutation parity failed: corners and edges have different parity.');
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
        cornerOrientationSum,
        edgeOrientationSum,
        cornerParity,
        edgeParity
    };
};

export const analyzeDiscreteCubeState = discreteState => {
    const validation = validateDiscreteCubeState(discreteState);
    const metrics = {
        validation,
        misplacedCorners: 0,
        twistedCorners: 0,
        misplacedEdges: 0,
        flippedEdges: 0,
        correctlyPlacedPieces: 0,
        phaseOneLowerBound: null,
        legalImmediateMoves: 18,
        nonReversingFutureMoves: 15,
        isSolved: false
    };

    if (!discreteState || !discreteState.corners || !discreteState.edges) {
        return metrics;
    }

    discreteState.corners.forEach((corner, index) => {
        if (corner.piece !== index) metrics.misplacedCorners++;
        if (corner.ori !== 0) metrics.twistedCorners++;
        if (corner.piece === index && corner.ori === 0) metrics.correctlyPlacedPieces++;
    });
    discreteState.edges.forEach((edge, index) => {
        if (edge.piece !== index) metrics.misplacedEdges++;
        if (edge.ori !== 0) metrics.flippedEdges++;
        if (edge.piece === index && edge.ori === 0) metrics.correctlyPlacedPieces++;
    });

    metrics.isSolved = metrics.correctlyPlacedPieces === 20;

    if (validation.isValid && cachedSolverTables) {
        const cubieState = toInternalCubieState(discreteState);
        const twist = cornerOrientationToIndex(cubieState.co);
        const flip = edgeOrientationToIndex(cubieState.eo);
        const slice = sliceCombinationToIndex(cubieState.ep, cachedSolverTables.sliceMaskToIndex);
        metrics.phaseOneLowerBound = Math.max(
            cachedSolverTables.twistSlicePruning[twist * SLICE_COMBINATION_COUNT + slice],
            cachedSolverTables.flipSlicePruning[flip * SLICE_COMBINATION_COUNT + slice]
        );
    }

    return metrics;
};

export const initializeDeterministicSolver = progressCallback => {
    if (cachedSolverTables) {
        if (progressCallback) progressCallback('Decision tables are ready.', 100);
        return cachedSolverTables;
    }

    const startedAt = Date.now();
    const report = (message, percent) => {
        if (progressCallback) progressCallback(message, percent);
    };

    report('Creating exact legal move transformations...', 5);
    const allMoveCubes = createAllMoveCubes();
    const sliceCombinations = createSliceCombinations();

    report('Building phase-one coordinate tables...', 15);
    const phaseOneTables = createPhaseOneMoveTables(allMoveCubes, sliceCombinations);

    report('Building orientation and slice pruning tables...', 35);
    const twistSlicePruning = buildProductPruningTable(
        phaseOneTables.twistMove,
        TWIST_COUNT,
        phaseOneTables.sliceMove,
        SLICE_COMBINATION_COUNT,
        18,
        0,
        sliceCombinations.goalIndex
    );
    const flipSlicePruning = buildProductPruningTable(
        phaseOneTables.flipMove,
        FLIP_COUNT,
        phaseOneTables.sliceMove,
        SLICE_COMBINATION_COUNT,
        18,
        0,
        sliceCombinations.goalIndex
    );

    report('Building phase-two permutation tables...', 62);
    const phaseTwoTables = createPhaseTwoMoveTables(allMoveCubes);

    report('Building final permutation pruning tables...', 75);
    const cornerSlicePruning = buildProductPruningTable(
        phaseTwoTables.cornerPermutationMove,
        CORNER_PERMUTATION_COUNT,
        phaseTwoTables.slicePermutationMove,
        SLICE_PERMUTATION_COUNT,
        10,
        0,
        0
    );
    const edgeSlicePruning = buildProductPruningTable(
        phaseTwoTables.edgePermutationMove,
        EDGE_PERMUTATION_COUNT,
        phaseTwoTables.slicePermutationMove,
        SLICE_PERMUTATION_COUNT,
        10,
        0,
        0
    );

    cachedSolverTables = {
        allMoveCubes,
        sliceMaskToIndex: sliceCombinations.maskToIndex,
        sliceCombinations: sliceCombinations.combinations,
        sliceGoalIndex: sliceCombinations.goalIndex,
        twistMove: phaseOneTables.twistMove,
        flipMove: phaseOneTables.flipMove,
        sliceMove: phaseOneTables.sliceMove,
        cornerPermutationMove: phaseTwoTables.cornerPermutationMove,
        edgePermutationMove: phaseTwoTables.edgePermutationMove,
        slicePermutationMove: phaseTwoTables.slicePermutationMove,
        twistSlicePruning,
        flipSlicePruning,
        cornerSlicePruning,
        edgeSlicePruning
    };

    cachedInitializationMetrics = {
        milliseconds: Date.now() - startedAt,
        tableBytes: measureTableBytes(cachedSolverTables)
    };

    report('Deterministic Decision Engine ready.', 100);
    return cachedSolverTables;
};

export const getSolverInitializationMetrics = () => cachedInitializationMetrics;

export const solveDiscreteCubeState = (discreteState, options) => {
    const solveOptions = options || {};
    const validation = validateDiscreteCubeState(discreteState);
    if (!validation.isValid) {
        return {
            solved: false,
            moves: [],
            notation: [],
            error: validation.errors.join(' '),
            validation
        };
    }

    if (discreteState.isSolved || isDiscreteSolved(discreteState)) {
        return {
            solved: true,
            moves: [],
            notation: [],
            phaseOneDepth: 0,
            phaseTwoDepth: 0,
            nodesVisited: 0,
            milliseconds: 0,
            validation
        };
    }

    const tables = initializeDeterministicSolver(solveOptions.onInitializeProgress);
    const initial = toInternalCubieState(discreteState);
    const startedAt = Date.now();
    const deadline = startedAt + (solveOptions.timeoutMilliseconds || 30000);
    const maximumPhaseOneDepth = solveOptions.maximumPhaseOneDepth || 12;
    const maximumPhaseTwoDepth = solveOptions.maximumPhaseTwoDepth || 18;
    const statistics = { nodesVisited: 0, deadline, timedOut: false };

    const initialTwist = cornerOrientationToIndex(initial.co);
    const initialFlip = edgeOrientationToIndex(initial.eo);
    const initialSlice = sliceCombinationToIndex(initial.ep, tables.sliceMaskToIndex);
    const initialLowerBound = phaseOneHeuristic(initialTwist, initialFlip, initialSlice, tables);
    const phaseOnePath = [];
    let foundResult = null;

    for (let depth = initialLowerBound;
        depth <= maximumPhaseOneDepth && !foundResult && !statistics.timedOut;
        depth++) {
        if (solveOptions.onSearchProgress) {
            solveOptions.onSearchProgress({
                phase: 1,
                depth,
                nodesVisited: statistics.nodesVisited,
                elapsedMilliseconds: Date.now() - startedAt
            });
        }

        foundResult = searchPhaseOne(
            initial,
            initialTwist,
            initialFlip,
            initialSlice,
            depth,
            -1,
            phaseOnePath,
            tables,
            maximumPhaseTwoDepth,
            statistics,
            solveOptions
        );
    }

    const milliseconds = Date.now() - startedAt;
    if (!foundResult) {
        return {
            solved: false,
            moves: [],
            notation: [],
            error: statistics.timedOut ?
                'The deterministic search reached its time limit.' :
                'No solution was found within the configured search limits.',
            nodesVisited: statistics.nodesVisited,
            milliseconds,
            validation
        };
    }

    const moves = foundResult.phaseOneMoves.concat(foundResult.phaseTwoMoves);
    return {
        solved: true,
        moves,
        notation: moves.map(notationForMoveIndex),
        phaseOneDepth: foundResult.phaseOneMoves.length,
        phaseTwoDepth: foundResult.phaseTwoMoves.length,
        nodesVisited: statistics.nodesVisited,
        milliseconds,
        validation
    };
};

export const applyMoveToDiscreteState = (discreteState, moveIndex) => {
    const internal = toInternalCubieState(discreteState);
    const moveCubes = createAllMoveCubes();
    const moved = applyMoveCubeToState(internal, moveCubes[moveIndex]);
    return fromInternalCubieState(moved);
};

const searchPhaseOne = (
    cubieState,
    twist,
    flip,
    slice,
    depthRemaining,
    previousFace,
    path,
    tables,
    maximumPhaseTwoDepth,
    statistics,
    options
) => {
    statistics.nodesVisited++;
    if ((statistics.nodesVisited & 8191) === 0 && Date.now() > statistics.deadline) {
        statistics.timedOut = true;
        return null;
    }

    const heuristic = phaseOneHeuristic(twist, flip, slice, tables);
    if (heuristic > depthRemaining) {
        return null;
    }

    if (depthRemaining === 0) {
        if (twist !== 0 || flip !== 0 || slice !== tables.sliceGoalIndex) {
            return null;
        }

        const phaseTwo = solvePhaseTwo(
            cubieState,
            maximumPhaseTwoDepth,
            statistics,
            tables,
            options
        );
        if (phaseTwo) {
            return {
                phaseOneMoves: path.slice(),
                phaseTwoMoves: phaseTwo
            };
        }
        return null;
    }

    for (let moveIndex = 0; moveIndex < 18; moveIndex++) {
        const face = Math.floor(moveIndex / 3);
        if (!isCanonicalNextFace(previousFace, face)) {
            continue;
        }

        const nextTwist = tables.twistMove[twist * 18 + moveIndex];
        const nextFlip = tables.flipMove[flip * 18 + moveIndex];
        const nextSlice = tables.sliceMove[slice * 18 + moveIndex];
        if (phaseOneHeuristic(nextTwist, nextFlip, nextSlice, tables) > depthRemaining - 1) {
            continue;
        }

        path.push(moveIndex);
        const nextState = applyMoveCubeToState(cubieState, tables.allMoveCubes[moveIndex]);
        const result = searchPhaseOne(
            nextState,
            nextTwist,
            nextFlip,
            nextSlice,
            depthRemaining - 1,
            face,
            path,
            tables,
            maximumPhaseTwoDepth,
            statistics,
            options
        );
        path.pop();

        if (result || statistics.timedOut) {
            return result;
        }
    }

    return null;
};

const solvePhaseTwo = (cubieState, maximumDepth, statistics, tables, options) => {
    const cornerPermutation = permutationToIndex(cubieState.cp);
    const edgePermutation = permutationToIndex(cubieState.ep.slice(0, 8));
    const slicePermutation = permutationToIndex(cubieState.ep.slice(8).map(piece => piece - 8));
    const lowerBound = phaseTwoHeuristic(
        cornerPermutation,
        edgePermutation,
        slicePermutation,
        tables
    );
    const path = [];

    for (let depth = lowerBound;
        depth <= maximumDepth && !statistics.timedOut;
        depth++) {
        if (options.onSearchProgress) {
            options.onSearchProgress({
                phase: 2,
                depth,
                nodesVisited: statistics.nodesVisited
            });
        }

        if (searchPhaseTwo(
            cornerPermutation,
            edgePermutation,
            slicePermutation,
            depth,
            -1,
            path,
            tables,
            statistics
        )) {
            return path.slice();
        }
    }

    return null;
};

const searchPhaseTwo = (
    cornerPermutation,
    edgePermutation,
    slicePermutation,
    depthRemaining,
    previousFace,
    path,
    tables,
    statistics
) => {
    statistics.nodesVisited++;
    if ((statistics.nodesVisited & 8191) === 0 && Date.now() > statistics.deadline) {
        statistics.timedOut = true;
        return false;
    }

    const heuristic = phaseTwoHeuristic(
        cornerPermutation,
        edgePermutation,
        slicePermutation,
        tables
    );
    if (heuristic > depthRemaining) {
        return false;
    }

    if (depthRemaining === 0) {
        return cornerPermutation === 0 && edgePermutation === 0 && slicePermutation === 0;
    }

    for (let localMoveIndex = 0; localMoveIndex < 10; localMoveIndex++) {
        const globalMoveIndex = PHASE_TWO_GLOBAL_MOVES[localMoveIndex];
        const face = Math.floor(globalMoveIndex / 3);
        if (!isCanonicalNextFace(previousFace, face)) {
            continue;
        }

        const nextCornerPermutation = tables.cornerPermutationMove[
            cornerPermutation * 10 + localMoveIndex
        ];
        const nextEdgePermutation = tables.edgePermutationMove[
            edgePermutation * 10 + localMoveIndex
        ];
        const nextSlicePermutation = tables.slicePermutationMove[
            slicePermutation * 10 + localMoveIndex
        ];

        if (phaseTwoHeuristic(
            nextCornerPermutation,
            nextEdgePermutation,
            nextSlicePermutation,
            tables
        ) > depthRemaining - 1) {
            continue;
        }

        path.push(globalMoveIndex);
        if (searchPhaseTwo(
            nextCornerPermutation,
            nextEdgePermutation,
            nextSlicePermutation,
            depthRemaining - 1,
            face,
            path,
            tables,
            statistics
        )) {
            return true;
        }
        path.pop();

        if (statistics.timedOut) {
            return false;
        }
    }

    return false;
};

const phaseOneHeuristic = (twist, flip, slice, tables) => Math.max(
    tables.twistSlicePruning[twist * SLICE_COMBINATION_COUNT + slice],
    tables.flipSlicePruning[flip * SLICE_COMBINATION_COUNT + slice]
);

const phaseTwoHeuristic = (cornerPermutation, edgePermutation, slicePermutation, tables) => Math.max(
    tables.cornerSlicePruning[
        cornerPermutation * SLICE_PERMUTATION_COUNT + slicePermutation
    ],
    tables.edgeSlicePruning[
        edgePermutation * SLICE_PERMUTATION_COUNT + slicePermutation
    ]
);

const isCanonicalNextFace = (previousFace, nextFace) => {
    if (previousFace < 0) return true;
    if (nextFace === previousFace) return false;

    // Opposite faces commute. Keeping one deterministic order removes the
    // duplicate D U / U D, B F / F B and L R / R L branches.
    const previousAxisFamily = Math.floor(previousFace / 2);
    const nextAxisFamily = Math.floor(nextFace / 2);
    return previousAxisFamily !== nextAxisFamily || nextFace > previousFace;
};

const createAllMoveCubes = () => {
    const moves = [];
    QUARTER_TURN_MOVE_CUBES.forEach(quarterTurn => {
        const halfTurn = composeMoveCubes(quarterTurn, quarterTurn);
        const inverseTurn = composeMoveCubes(halfTurn, quarterTurn);
        moves.push(cloneMoveCube(quarterTurn), halfTurn, inverseTurn);
    });
    return moves;
};

const cloneMoveCube = move => ({
    cp: move.cp.slice(),
    co: move.co.slice(),
    ep: move.ep.slice(),
    eo: move.eo.slice()
});

const composeMoveCubes = (first, second) => {
    const composed = {
        cp: new Array(8),
        co: new Array(8),
        ep: new Array(12),
        eo: new Array(12)
    };

    for (let slot = 0; slot < 8; slot++) {
        const intermediateSlot = second.cp[slot];
        composed.cp[slot] = first.cp[intermediateSlot];
        composed.co[slot] = (first.co[intermediateSlot] + second.co[slot]) % 3;
    }
    for (let slot = 0; slot < 12; slot++) {
        const intermediateSlot = second.ep[slot];
        composed.ep[slot] = first.ep[intermediateSlot];
        composed.eo[slot] = (first.eo[intermediateSlot] + second.eo[slot]) % 2;
    }

    return composed;
};

const applyMoveCubeToState = (state, move) => {
    const next = {
        cp: new Array(8),
        co: new Array(8),
        ep: new Array(12),
        eo: new Array(12)
    };

    for (let slot = 0; slot < 8; slot++) {
        const sourceSlot = move.cp[slot];
        next.cp[slot] = state.cp[sourceSlot];
        next.co[slot] = (state.co[sourceSlot] + move.co[slot]) % 3;
    }
    for (let slot = 0; slot < 12; slot++) {
        const sourceSlot = move.ep[slot];
        next.ep[slot] = state.ep[sourceSlot];
        next.eo[slot] = (state.eo[sourceSlot] + move.eo[slot]) % 2;
    }

    return next;
};

const createPhaseOneMoveTables = (moves, sliceData) => {
    const twistMove = new Uint16Array(TWIST_COUNT * 18);
    const flipMove = new Uint16Array(FLIP_COUNT * 18);
    const sliceMove = new Uint16Array(SLICE_COMBINATION_COUNT * 18);

    for (let twist = 0; twist < TWIST_COUNT; twist++) {
        const state = solvedInternalState();
        state.co = indexToCornerOrientation(twist);
        for (let moveIndex = 0; moveIndex < 18; moveIndex++) {
            twistMove[twist * 18 + moveIndex] = cornerOrientationToIndex(
                applyMoveCubeToState(state, moves[moveIndex]).co
            );
        }
    }

    for (let flip = 0; flip < FLIP_COUNT; flip++) {
        const state = solvedInternalState();
        state.eo = indexToEdgeOrientation(flip);
        for (let moveIndex = 0; moveIndex < 18; moveIndex++) {
            flipMove[flip * 18 + moveIndex] = edgeOrientationToIndex(
                applyMoveCubeToState(state, moves[moveIndex]).eo
            );
        }
    }

    for (let slice = 0; slice < SLICE_COMBINATION_COUNT; slice++) {
        const state = solvedInternalState();
        state.ep = edgePermutationForSlicePositions(sliceData.combinations[slice]);
        for (let moveIndex = 0; moveIndex < 18; moveIndex++) {
            const moved = applyMoveCubeToState(state, moves[moveIndex]);
            sliceMove[slice * 18 + moveIndex] = sliceCombinationToIndex(
                moved.ep,
                sliceData.maskToIndex
            );
        }
    }

    return { twistMove, flipMove, sliceMove };
};

const createPhaseTwoMoveTables = moves => {
    const cornerPermutationMove = new Uint16Array(CORNER_PERMUTATION_COUNT * 10);
    const edgePermutationMove = new Uint16Array(EDGE_PERMUTATION_COUNT * 10);
    const slicePermutationMove = new Uint8Array(SLICE_PERMUTATION_COUNT * 10);

    for (let permutationIndex = 0;
        permutationIndex < CORNER_PERMUTATION_COUNT;
        permutationIndex++) {
        const state = solvedInternalState();
        state.cp = indexToPermutation(permutationIndex, 8);
        for (let localMoveIndex = 0; localMoveIndex < 10; localMoveIndex++) {
            const move = moves[PHASE_TWO_GLOBAL_MOVES[localMoveIndex]];
            cornerPermutationMove[permutationIndex * 10 + localMoveIndex] =
                permutationToIndex(applyMoveCubeToState(state, move).cp);
        }
    }

    for (let permutationIndex = 0;
        permutationIndex < EDGE_PERMUTATION_COUNT;
        permutationIndex++) {
        const state = solvedInternalState();
        state.ep = indexToPermutation(permutationIndex, 8).concat([8, 9, 10, 11]);
        for (let localMoveIndex = 0; localMoveIndex < 10; localMoveIndex++) {
            const move = moves[PHASE_TWO_GLOBAL_MOVES[localMoveIndex]];
            const moved = applyMoveCubeToState(state, move);
            edgePermutationMove[permutationIndex * 10 + localMoveIndex] =
                permutationToIndex(moved.ep.slice(0, 8));
        }
    }

    for (let permutationIndex = 0;
        permutationIndex < SLICE_PERMUTATION_COUNT;
        permutationIndex++) {
        const state = solvedInternalState();
        state.ep = [0, 1, 2, 3, 4, 5, 6, 7].concat(
            indexToPermutation(permutationIndex, 4).map(piece => piece + 8)
        );
        for (let localMoveIndex = 0; localMoveIndex < 10; localMoveIndex++) {
            const move = moves[PHASE_TWO_GLOBAL_MOVES[localMoveIndex]];
            const moved = applyMoveCubeToState(state, move);
            slicePermutationMove[permutationIndex * 10 + localMoveIndex] =
                permutationToIndex(moved.ep.slice(8).map(piece => piece - 8));
        }
    }

    return {
        cornerPermutationMove,
        edgePermutationMove,
        slicePermutationMove
    };
};

const buildProductPruningTable = (
    firstMoveTable,
    firstCount,
    secondMoveTable,
    secondCount,
    moveCount,
    goalFirst,
    goalSecond
) => {
    const totalCount = firstCount * secondCount;
    const pruning = new Uint8Array(totalCount);
    pruning.fill(UNVISITED);
    const queue = new Int32Array(totalCount);
    const goalIndex = goalFirst * secondCount + goalSecond;
    pruning[goalIndex] = 0;
    queue[0] = goalIndex;
    let head = 0;
    let tail = 1;

    while (head < tail) {
        const combinedIndex = queue[head++];
        const first = Math.floor(combinedIndex / secondCount);
        const second = combinedIndex - first * secondCount;
        const nextDepth = pruning[combinedIndex] + 1;
        const firstOffset = first * moveCount;
        const secondOffset = second * moveCount;

        for (let moveIndex = 0; moveIndex < moveCount; moveIndex++) {
            const nextFirst = firstMoveTable[firstOffset + moveIndex];
            const nextSecond = secondMoveTable[secondOffset + moveIndex];
            const nextCombinedIndex = nextFirst * secondCount + nextSecond;
            if (pruning[nextCombinedIndex] === UNVISITED) {
                pruning[nextCombinedIndex] = nextDepth;
                queue[tail++] = nextCombinedIndex;
            }
        }
    }

    return pruning;
};

const createSliceCombinations = () => {
    const combinations = [];
    const maskToIndex = new Int16Array(1 << 12);
    maskToIndex.fill(-1);

    const addCombination = (start, selected) => {
        if (selected.length === 4) {
            const combination = selected.slice();
            let mask = 0;
            combination.forEach(position => { mask |= (1 << position); });
            maskToIndex[mask] = combinations.length;
            combinations.push(combination);
            return;
        }

        for (let position = start;
            position <= 12 - (4 - selected.length);
            position++) {
            selected.push(position);
            addCombination(position + 1, selected);
            selected.pop();
        }
    };

    addCombination(0, []);
    const goalMask = (1 << 8) | (1 << 9) | (1 << 10) | (1 << 11);
    return {
        combinations,
        maskToIndex,
        goalIndex: maskToIndex[goalMask]
    };
};

const edgePermutationForSlicePositions = slicePositions => {
    const isSlicePosition = {};
    slicePositions.forEach(position => { isSlicePosition[position] = true; });
    const permutation = new Array(12);
    let ordinaryPiece = 0;
    let slicePiece = 8;

    for (let position = 0; position < 12; position++) {
        permutation[position] = isSlicePosition[position] ? slicePiece++ : ordinaryPiece++;
    }
    return permutation;
};

const cornerOrientationToIndex = orientation => {
    let index = 0;
    for (let slot = 0; slot < 7; slot++) {
        index = index * 3 + orientation[slot];
    }
    return index;
};

const indexToCornerOrientation = index => {
    const orientation = new Array(8).fill(0);
    let sum = 0;
    for (let slot = 6; slot >= 0; slot--) {
        orientation[slot] = index % 3;
        sum += orientation[slot];
        index = Math.floor(index / 3);
    }
    orientation[7] = (3 - (sum % 3)) % 3;
    return orientation;
};

const edgeOrientationToIndex = orientation => {
    let index = 0;
    for (let slot = 0; slot < 11; slot++) {
        index = index * 2 + orientation[slot];
    }
    return index;
};

const indexToEdgeOrientation = index => {
    const orientation = new Array(12).fill(0);
    let sum = 0;
    for (let slot = 10; slot >= 0; slot--) {
        orientation[slot] = index % 2;
        sum += orientation[slot];
        index = Math.floor(index / 2);
    }
    orientation[11] = sum % 2;
    return orientation;
};

const sliceCombinationToIndex = (edgePermutation, maskToIndex) => {
    let mask = 0;
    for (let position = 0; position < 12; position++) {
        if (edgePermutation[position] >= SLICE_EDGE_FIRST) {
            mask |= (1 << position);
        }
    }
    return maskToIndex[mask];
};

const permutationToIndex = permutation => {
    let index = 0;
    const length = permutation.length;
    for (let position = 0; position < length; position++) {
        let smallerToRight = 0;
        for (let following = position + 1; following < length; following++) {
            if (permutation[following] < permutation[position]) {
                smallerToRight++;
            }
        }
        index += smallerToRight * FACTORIAL[length - position - 1];
    }
    return index;
};

const indexToPermutation = (index, length) => {
    const available = Array(length).fill(0).map((unused, value) => value);
    const permutation = new Array(length);

    for (let position = 0; position < length; position++) {
        const blockSize = FACTORIAL[length - position - 1];
        const selectedIndex = Math.floor(index / blockSize);
        index %= blockSize;
        permutation[position] = available.splice(selectedIndex, 1)[0];
    }
    return permutation;
};

const solvedInternalState = () => ({
    cp: [0, 1, 2, 3, 4, 5, 6, 7],
    co: [0, 0, 0, 0, 0, 0, 0, 0],
    ep: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    eo: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
});

const toInternalCubieState = discreteState => ({
    cp: discreteState.corners.map(corner => corner.piece),
    co: discreteState.corners.map(corner => corner.ori),
    ep: discreteState.edges.map(edge => edge.piece),
    eo: discreteState.edges.map(edge => edge.ori)
});

const fromInternalCubieState = state => ({
    corners: state.cp.map((piece, slot) => ({ piece, ori: state.co[slot] })),
    edges: state.ep.map((piece, slot) => ({ piece, ori: state.eo[slot] })),
    isSolved: state.cp.every((piece, slot) => piece === slot && state.co[slot] === 0) &&
        state.ep.every((piece, slot) => piece === slot && state.eo[slot] === 0)
});

const isDiscreteSolved = state => state.corners.every(
    (corner, slot) => corner.piece === slot && corner.ori === 0
) && state.edges.every(
    (edge, slot) => edge.piece === slot && edge.ori === 0
);

const isIntegerInRange = (value, minimum, maximum) => (
    typeof value === 'number' && isFinite(value) &&
    Math.floor(value) === value && value >= minimum && value <= maximum
);

const containsEveryIdentityExactlyOnce = (values, count) => {
    if (!values || values.length !== count) return false;
    const seen = new Array(count).fill(false);
    for (let index = 0; index < values.length; index++) {
        const value = values[index];
        if (!isIntegerInRange(value, 0, count - 1) || seen[value]) {
            return false;
        }
        seen[value] = true;
    }
    return true;
};

const permutationParity = permutation => {
    let inversions = 0;
    for (let first = 0; first < permutation.length; first++) {
        for (let second = first + 1; second < permutation.length; second++) {
            if (permutation[first] > permutation[second]) inversions++;
        }
    }
    return inversions % 2;
};

const measureTableBytes = tables => Object.keys(tables).reduce((total, key) => {
    const value = tables[key];
    return total + (value && value.byteLength ? value.byteLength : 0);
}, 0);

export const __solverInternals = {
    createAllMoveCubes,
    applyMoveCubeToState,
    permutationToIndex,
    indexToPermutation,
    cornerOrientationToIndex,
    indexToCornerOrientation,
    edgeOrientationToIndex,
    indexToEdgeOrientation,
    createSliceCombinations
};
